import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';

export default function VideoTutorialPage({ tenantOverride = null }) {
  const themeContext = useTheme();
  const contextTenantConfig = themeContext?.tenantConfig || null;
  const [tenantData, setTenantData] = useState(tenantOverride || contextTenantConfig);
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  // Check URL query parameters for explicit ?tenant= or ?tenant_id=
  useEffect(() => {
    if (tenantOverride) {
      setTenantData(tenantOverride);
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const pathTenantMatch = window.location.pathname.match(/^\/tutorial\/([a-zA-Z0-9_-]+)/);
    const tenantParam = params.get('tenant') || params.get('tenant_id') || (pathTenantMatch ? pathTenantMatch[1] : null);

    const isDev = window.location.port === '3000';
    const API_BASE = isDev ? `http://${window.location.hostname}:8000` : '';

    if (tenantParam && (!contextTenantConfig || String(contextTenantConfig.id) !== tenantParam)) {
      fetch(`${API_BASE}/api/v1/tenant/config?tenant_id=${tenantParam}`, {
        headers: { 'X-Tenant-Domain': window.location.hostname }
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data && data.brand_name) {
            setTenantData(data);
          }
        })
        .catch(() => {});
    } else if (contextTenantConfig) {
      setTenantData(contextTenantConfig);
    } else {
      fetch(`${API_BASE}/api/v1/tenant/config`, {
        headers: { 'X-Tenant-Domain': window.location.hostname }
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data && data.brand_name) {
            setTenantData(data);
          }
        })
        .catch(() => {});
    }
  }, [contextTenantConfig, tenantOverride]);

  const isDev = typeof window !== 'undefined' && window.location.port === '3000';
  const API_BASE = process.env.REACT_APP_API_BASE || (isDev ? `http://${window.location.hostname}:8000` : '');

  const videoUrl = tenantData?.onboarding_video_url || '';
  const resolvedVideoUrl = (videoUrl && videoUrl.startsWith('/uploads/') && API_BASE)
    ? `${API_BASE.replace(/\/+$/, '')}${videoUrl}`
    : videoUrl;

  const rawLogoUrl = tenantData?.logo_url || '';
  const resolvedLogoUrl = (rawLogoUrl && rawLogoUrl.startsWith('/uploads/') && API_BASE)
    ? `${API_BASE.replace(/\/+$/, '')}${rawLogoUrl}`
    : rawLogoUrl;

  const videoTitle = tenantData?.onboarding_video_title || 'How to Log Your Trophy Coin';
  const brandName = tenantData?.brand_name || 'TrophyCoin';

  // Check if user navigated here from login page (via ?from=login or document.referrer)
  const currentParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const fromLoginParam = currentParams.get('from') === 'login';
  const referrerFromLogin = typeof document !== 'undefined' && Boolean(
    document.referrer && (
      document.referrer.includes('/login') ||
      document.referrer.includes('/auth') ||
      document.referrer.includes('/c/')
    )
  );
  const isFromLogin = fromLoginParam || referrerFromLogin;

  const handleBack = () => {
    if (typeof window !== 'undefined') {
      if (window.history.length > 1) {
        window.history.back();
      } else if (window.opener) {
        window.close();
      } else if (document.referrer) {
        window.location.href = document.referrer;
      } else {
        window.location.href = '/';
      }
    }
  };

  // Determine if video is YouTube, Vimeo, or HTML5 MP4/WebM
  const isYouTube = videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be');
  const isVimeo = videoUrl.includes('vimeo.com');

  const getYouTubeEmbedUrl = (url) => {
    try {
      if (url.includes('youtu.be/')) {
        const id = url.split('youtu.be/')[1]?.split('?')[0];
        return `https://www.youtube.com/embed/${id}?autoplay=1&rel=0`;
      }
      const match = url.match(/[?&]v=([^&]+)/);
      if (match && match[1]) {
        return `https://www.youtube.com/embed/${match[1]}?autoplay=1&rel=0`;
      }
    } catch (e) {}
    return url;
  };

  const getVimeoEmbedUrl = (url) => {
    try {
      const match = url.match(/vimeo\.com\/(\d+)/);
      if (match && match[1]) {
        return `https://player.vimeo.com/video/${match[1]}?autoplay=1`;
      }
    } catch (e) {}
    return url;
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0a0d14',
      color: '#f0f6fc',
      fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* CONDITIONAL TOP BACK BAR - Only shown if navigated from login page */}
      {isFromLogin && (
        <div style={{
          padding: '16px 20px 0 20px',
          maxWidth: '1000px',
          width: '100%',
          margin: '0 auto',
          boxSizing: 'border-box',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center'
        }}>
          <button
            type="button"
            onClick={handleBack}
            style={{
              padding: '7px 14px',
              borderRadius: '6px',
              border: '1px solid #30363d',
              backgroundColor: '#161b22',
              color: '#c9d1d9',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.15s'
            }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#21262d'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#161b22'; e.currentTarget.style.color = '#c9d1d9'; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            <span>Back</span>
          </button>
        </div>
      )}

      {/* MAIN CONTENT AREA */}
      <main style={{
        flex: 1,
        maxWidth: '1000px',
        width: '100%',
        margin: '0 auto',
        padding: isFromLogin ? '16px 20px 60px 20px' : '36px 20px 60px 20px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
      }}>
        {/* BRAND LOGO - Rendered in the exact same way as the Form Wizard login page, without tenant text */}
        {resolvedLogoUrl ? (
          <div style={{
            width: '100%',
            maxWidth: '380px',
            boxSizing: 'border-box',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: '0 auto',
            padding: '0 6px'
          }}>
            <img
              src={resolvedLogoUrl}
              alt="Logo"
              style={{
                width: 'auto',
                maxWidth: '100%',
                maxHeight: '130px',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: '12px',
                overflow: 'hidden',
                filter: 'drop-shadow(0 4px 14px rgba(0,0,0,0.4))'
              }}
            />
          </div>
        ) : null}

        {/* TITLE & INSTRUCTIONS */}
        <div style={{ textAlign: 'center', maxWidth: '750px', margin: '0 auto' }}>
          <h1 style={{
            fontSize: '26px',
            fontWeight: '800',
            color: '#fff',
            marginBottom: '8px',
            lineHeight: '1.25'
          }}>
            {videoTitle}
          </h1>
          <p style={{
            fontSize: '14px',
            color: '#8b949e',
            lineHeight: '1.5',
            margin: 0
          }}>
            Watch this onboarding walkthrough to learn how to scan your physical coin, sign in, and log your details using the Form Wizard.
          </p>
        </div>

        {/* VIDEO PLAYER CONTAINER */}
        <div 
          ref={containerRef}
          style={{
            position: 'relative',
            width: '100%',
            backgroundColor: '#000',
            borderRadius: '12px',
            overflow: 'hidden',
            border: '1px solid #30363d',
            boxShadow: '0 12px 36px rgba(0, 0, 0, 0.55)',
            aspectRatio: '16/9',
            maxHeight: '68vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {videoUrl ? (
            isYouTube ? (
              <iframe
                title={videoTitle}
                src={getYouTubeEmbedUrl(videoUrl)}
                style={{ width: '100%', height: '100%', border: 'none' }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
              />
            ) : isVimeo ? (
              <iframe
                title={videoTitle}
                src={getVimeoEmbedUrl(videoUrl)}
                style={{ width: '100%', height: '100%', border: 'none' }}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                {videoError ? (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    padding: '24px',
                    textAlign: 'center',
                    color: '#c9d1d9'
                  }}>
                    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#f85149" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '14px' }}>
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#fff', marginBottom: '8px' }}>
                      Playback Interrupted
                    </div>
                    <p style={{ fontSize: '13px', color: '#8b949e', marginBottom: '20px', maxWidth: '420px', lineHeight: '1.5' }}>
                      The media stream could not be loaded. Please ensure you have a stable network connection, or try reloading the video stream.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setVideoError(false);
                        if (videoRef.current) {
                          videoRef.current.load();
                        }
                      }}
                      style={{
                        padding: '8px 20px',
                        backgroundColor: '#238636',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="23 4 23 10 17 10" />
                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                      </svg>
                      <span>Reload Video</span>
                    </button>
                  </div>
                ) : (
                  <video
                    key={resolvedVideoUrl}
                    ref={videoRef}
                    src={resolvedVideoUrl}
                    controls
                    playsInline
                    preload="metadata"
                    onError={() => setVideoError(true)}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      backgroundColor: '#000'
                    }}
                  >
                    Your browser does not support HTML5 video streaming.
                  </video>
                )}
              </div>
            )
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#8b949e',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                backgroundColor: '#21262d',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#8b949e'
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="23 7 16 12 23 17 23 7" />
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                </svg>
              </div>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#c9d1d9' }}>
                No Video Configured Yet
              </div>
              <div style={{ fontSize: '13px', maxWidth: '380px', lineHeight: '1.4' }}>
                The administrator for {brandName} can upload or link an onboarding video under <strong>Settings → Site Styles &amp; Branding → Onboarding &amp; Tutorial Video</strong>.
              </div>
            </div>
          )}
        </div>

        {/* 4-STEP QUICK REFERENCE GUIDE */}
        <div style={{
          backgroundColor: '#161b22',
          border: '1px solid #30363d',
          borderRadius: '12px',
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '18px'
        }}>
          <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#fff', margin: 0 }}>
            Quick 4-Step Summary
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px'
          }}>
            {[
              {
                step: '1',
                title: 'Scan NFC or QR',
                desc: 'Tap your phone on the coin or scan the back QR code to open the coin portal.'
              },
              {
                step: '2',
                title: 'Sign In to Account',
                desc: 'Log in with your authorized angler or organization operator credentials.'
              },
              {
                step: '3',
                title: 'Complete Wizard',
                desc: 'Fill in the required catch records, date, photos, and tournament details.'
              },
              {
                step: '4',
                title: 'Verify & Mint',
                desc: 'Preview the photorealistic 3D coin and lock in the immutable record.'
              }
            ].map(item => (
              <div key={item.step} style={{
                backgroundColor: '#0d1117',
                border: '1px solid #21262d',
                borderRadius: '8px',
                padding: '14px',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div style={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(56, 139, 253, 0.2)',
                    color: '#58a6ff',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {item.step}
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#e6edf3' }}>
                    {item.title}
                  </div>
                </div>
                <div style={{ fontSize: '12px', color: '#8b949e', lineHeight: '1.4' }}>
                  {item.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* FOOTER */}
      <footer style={{
        padding: '20px',
        textAlign: 'center',
        borderTop: '1px solid #21262d',
        backgroundColor: '#161b22',
        color: '#8b949e',
        fontSize: '12px'
      }}>
        © {new Date().getFullYear()} {brandName}. All rights reserved.
      </footer>
    </div>
  );
}
